import React, { useEffect, useRef, useState } from 'react';
import PrimaryModal from '../UI/Modal/PrimaryModal';
import { PrimaryButton, SecondaryButton, ErrorButton } from "../UI";
import Input from '../UI/Input';
import AgGridWrapper from '../UI/AgGrid';
import { Trash2 } from 'lucide-react';
import ProjectPromptsModal from './ProjectPromptsModal';
import ProjectContextModal from './ProjectContextModal';
import { getProjects, createProject, deleteProject } from '../../utils/APIs';

const errorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

export default function ProjectPicker({
    token,
    organizationId,
    disabled,
    onSelectProject,
    initialProjectId,
    isManagerOpen: externalManagerOpen,
    onManagerOpenChange,
}) {
    const [promptsId, setPromptsId] = useState(null);
    const [contextId, setContextId] = useState(null);
    const [internalManagerOpen, setInternalManagerOpen] = useState(false);
    const managerOpen = externalManagerOpen !== undefined ? externalManagerOpen : internalManagerOpen;
    const setManagerOpen = (next) => {
        if (externalManagerOpen !== undefined) {
            onManagerOpenChange?.(next);
        } else {
            setInternalManagerOpen(next);
        }
    };
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteError, setDeleteError] = useState('');
    const [deleting, setDeleting] = useState(false);
    const deleteRequest = useRef(null);
    const [projects, setProjects] = useState([]);
    const [selectedId, setSelectedId] = useState(initialProjectId || '');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reload, setReload] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [createError, setCreateError] = useState('');
    const createRequest = useRef(null);
    const selectedProject = projects.find((project) => project._id === selectedId);
    useEffect(() => {
        if (loading) return;
        onSelectProject?.(selectedId || null, selectedProject?.name || '', selectedProject?.prompts);
    }, [selectedId, selectedProject, onSelectProject, loading]);

    useEffect(() => () => { createRequest.current?.abort(); deleteRequest.current?.abort(); }, []);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError('');
        if (!token || !organizationId) {
            setProjects([]);
            setSelectedId('');
            setLoading(false);
            return () => controller.abort();
        }
        getProjects(token, organizationId, controller.signal)
            .then((result) => {
                if (controller.signal.aborted) return;
                const items = result.data || [];
                setProjects(items);
                setSelectedId((id) => {
                    if (items.some((item) => item._id === id)) return id;
                    if (items.some((item) => item._id === initialProjectId)) return initialProjectId;
                    return initialProjectId === null ? '' : (items[0]?._id || '');
                });
            })
            .catch((err) => { if (!controller.signal.aborted) setError(errorMessage(err, 'Unable to load projects.')); })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [token, organizationId, reload, initialProjectId]);

    const openModal = () => { setName(''); setCreateError(''); setModalOpen(true); };
    const submit = async (event) => {
        event.preventDefault();
        if (!name.trim() || createRequest.current) return;
        const controller = new AbortController();
        createRequest.current = controller;
        setSaving(true);
        setCreateError('');
        try {
            const result = await createProject(token, organizationId, { name: name.trim(), context: [] }, controller.signal);
            if (controller.signal.aborted) return;
            setProjects((items) => [result.data, ...items]);
            setSelectedId(result.data._id);
            setModalOpen(false);
        } catch (err) {
            if (!controller.signal.aborted) setCreateError(errorMessage(err, 'Unable to create project.'));
        } finally {
            if (!controller.signal.aborted) { setSaving(false); createRequest.current = null; }
        }
    };

    const removeProject = async (id) => {
        if (deleteRequest.current) return;
        const controller = new AbortController();
        deleteRequest.current = controller;
        setDeleting(true);
        setDeleteError('');
        try {
            await deleteProject(token, organizationId, id, controller.signal);
            if (controller.signal.aborted) return;
            setDeleteTarget(null);
            const remaining = projects.filter((project) => project._id !== id);
            setProjects(remaining);
            setSelectedId((current) => current === id ? (remaining[0]?._id || '') : current);
        } catch (err) {
            if (!controller.signal.aborted) setDeleteError(errorMessage(err, 'Unable to delete project.'));
        } finally {
            if (!controller.signal.aborted) { setDeleting(false); deleteRequest.current = null; }
        }
    };

    const handleCellClicked = (params) => {
        if (deleting || !params?.data?._id) return;
        const colField = params.colDef?.field;
        const colId = params.colDef?.colId;
        const headerName = params.colDef?.headerName;
        if (colId === 'select' || headerName === 'Select' || colField === 'name' || headerName === 'Project') {
            setSelectedId(params.data._id);
        }
    };

    return (
        <>
            <SecondaryButton type="button" size="xs" disable={disabled || !organizationId || !token} handleClick={() => setManagerOpen(true)}>Manage project</SecondaryButton>
            {managerOpen && !modalOpen && !contextId && !promptsId && !deleteTarget && (
                <PrimaryModal title="Manage projects" zIndex={1300} loading={deleting} hideModal={() => setManagerOpen(false)} widthClass="!max-w-5xl">
                    <div className="p-6">
                        <div className="flex justify-end mb-4"><PrimaryButton size="sm" disable={deleting || loading} handleClick={openModal}>Create new project</PrimaryButton></div>
                        {loading ? <p role="status">Loading projects…</p> : <AgGridWrapper
                            tableId="assistant-projects"
                            rowData={projects}
                            getRowId={({ data }) => data._id}
                            rowHeight={56}
                            defaultColDef={{ sortable: true, resizable: true }}
                            columnDefs={[
                                {
                                    headerName: 'Select',
                                    colId: 'select',
                                    width: 85,
                                    sortable: false,
                                    cellStyle: { display: 'flex', alignItems: 'center', cursor: 'pointer' },
                                    cellClass: 'cursor-pointer',
                                    onCellClicked: ({ data }) => {
                                        if (!deleting && data?._id) setSelectedId(data._id);
                                    },
                                    cellRenderer: ({ data }) => (
                                        <input
                                            aria-label={`Select ${data.name}`}
                                            type="radio"
                                            value={data._id}
                                            name="assistant-project"
                                            className="accent-primary cursor-pointer"
                                            checked={selectedId === data._id}
                                            disabled={deleting}
                                            onChange={() => setSelectedId(data._id)}
                                        />
                                    ),
                                },
                                {
                                    headerName: 'Project',
                                    field: 'name',
                                    flex: 1,
                                    minWidth: 160,
                                    cellStyle: { display: 'flex', alignItems: 'center', cursor: 'pointer' },
                                    cellClass: 'cursor-pointer',
                                    onCellClicked: ({ data }) => {
                                        if (!deleting && data?._id) setSelectedId(data._id);
                                    },
                                },
                                { headerName: 'Manage context', cellStyle: { display: 'flex', alignItems: 'center' }, width: 170, sortable: false, cellRenderer: ({ data }) => <SecondaryButton size="xs" disable={deleting} handleClick={() => setContextId(data._id)}>Manage context</SecondaryButton> },
                                { headerName: 'Manage prompts', cellStyle: { display: 'flex', alignItems: 'center' }, width: 170, sortable: false, cellRenderer: ({ data }) => <SecondaryButton size="xs" disable={deleting} handleClick={() => setPromptsId(data._id)}>Manage prompts</SecondaryButton> },
                                { headerName: 'Delete', cellStyle: { display: 'flex', alignItems: 'center' }, width: 100, sortable: false, cellRenderer: ({ data }) => <ErrorButton size="xs" className="!min-w-0" disable={deleting} handleClick={() => { setDeleteError(''); setDeleteTarget(data); }}><Trash2 size={18} aria-hidden="true" /><span className="sr-only">Delete project {data.name}</span></ErrorButton> },
                            ]}
                            onCellClicked={handleCellClicked}
                            overlayNoRowsTemplate="No projects in this organisation."
                        />}
                        {error && <div role="alert" className="text-sm text-danger">{error}<SecondaryButton size="xs" disable={deleting} handleClick={() => setReload((n) => n + 1)}>Retry</SecondaryButton></div>}
                        <div className="flex justify-end mt-4"><SecondaryButton size="sm" disable={deleting} handleClick={() => setManagerOpen(false)}>Done</SecondaryButton></div>
                    </div>
                </PrimaryModal>
            )}
            {deleteTarget && <PrimaryModal title="Delete project?" zIndex={1300} loading={deleting} hideModal={() => setDeleteTarget(null)}>
                <div className="p-6">
                    <p>Deleting “{deleteTarget.name}” will permanently delete the project and all associated context files. This cannot be undone.</p>
                    {deleteError && <p role="alert" className="mt-3 text-sm text-danger">{deleteError}</p>}
                    <div className="flex justify-end gap-3 mt-6">
                        <SecondaryButton size="sm" disable={deleting} handleClick={() => setDeleteTarget(null)}>Cancel</SecondaryButton>
                        <ErrorButton size="sm" loading={deleting} handleClick={() => removeProject(deleteTarget._id)}>Delete project</ErrorButton>
                    </div>
                </div>
            </PrimaryModal>}
            {promptsId && <ProjectPromptsModal key={promptsId} token={token} organizationId={organizationId} projectId={promptsId} onClose={() => setPromptsId(null)} onUpdated={(project) => setProjects((items) => items.map((item) => item._id === project._id ? project : item))} />}
            {contextId && <ProjectContextModal key={contextId} token={token} organizationId={organizationId} projectId={contextId} onClose={() => setContextId(null)} onUpdated={(project) => setProjects((items) => items.map((item) => item._id === project._id ? project : item))} />}
            {modalOpen && <PrimaryModal title="Create new project" zIndex={1300} loading={saving} hideModal={() => setModalOpen(false)}>
                <form onSubmit={submit} className="p-6">
                    <Input
                        id="project-name"
                        label="Project name"
                        mandatory
                        required
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        disabled={saving}
                        error={createError}
                    />
                    <div className="flex justify-end gap-3 mt-6">
                        <SecondaryButton type="button" size="sm" disable={saving} handleClick={() => setModalOpen(false)}>Cancel</SecondaryButton>
                        <PrimaryButton type="submit" size="sm" loading={saving} disable={!name.trim()}>Create project</PrimaryButton>
                    </div>
                </form>
            </PrimaryModal>}
        </>
    );
}
